// Authentication utilities for handling Devise API calls

interface User {
  id: number;
  first_name: string;
  email: string;
  admin: boolean;
}

interface AuthResponse {
  success: boolean;
  user?: User;
  errors?: string[];
  message?: string;
}

// Read a cookie by name
function getCookie(name: string): string | null {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop()?.split(';').shift() || null;
  }
  return null;
}

// Get CSRF token (prefer cookie over meta tag since cookie is updated by server)
function getCsrfToken(): string {
  // Try cookie first (updated after logout and other server actions)
  const cookieToken = getCookie('CSRF-TOKEN');
  if (cookieToken) {
    return cookieToken;
  }

  // Fall back to meta tag
  const metaToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
  return metaToken || '';
}

// Refresh CSRF token (re-read from cookie/meta tag)
function refreshCsrfToken(): string {
  // Always prefer cookie over meta tag
  const cookieToken = getCookie('CSRF-TOKEN');
  if (cookieToken) {
    return cookieToken;
  }

  const metaToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
  return metaToken || '';
}

// Base fetch wrapper with CSRF token and retry logic
async function authFetch(
  url: string,
  options: RequestInit = {},
  retry: boolean = true
): Promise<Response> {
  const csrfToken = getCsrfToken();

  const headers = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-CSRF-Token': csrfToken,
    ...options.headers,
  };

  const response = await fetch(url, {
    ...options,
    headers,
    credentials: 'same-origin',
  });

  // If we get a 422 (CSRF error) and haven't retried yet, try once more with fresh token
  if (response.status === 422 && retry) {
    // Get fresh CSRF token from cookie/meta tag and retry
    const freshToken = refreshCsrfToken();

    // Only retry if we got a different token
    if (freshToken && freshToken !== csrfToken) {
      const retryHeaders = {
        ...headers,
        'X-CSRF-Token': freshToken,
      };

      return fetch(url, {
        ...options,
        headers: retryHeaders,
        credentials: 'same-origin',
      });
    }
  }

  return response;
}

// Login user
export async function login(email: string, password: string): Promise<AuthResponse> {
  try {
    const response = await authFetch('/users/sign_in', {
      method: 'POST',
      body: JSON.stringify({
        user: {
          email,
          password,
        },
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        errors: data.errors || ['Login failed'],
      };
    }

    return data;
  } catch (error) {
    return {
      success: false,
      errors: ['Network error. Please try again.'],
    };
  }
}

// Register new user
export async function register(
  email: string,
  password: string,
  passwordConfirmation: string
): Promise<AuthResponse> {
  try {
    const response = await authFetch('/users', {
      method: 'POST',
      body: JSON.stringify({
        user: {
          email,
          password,
          password_confirmation: passwordConfirmation,
        },
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        errors: data.errors || ['Registration failed'],
      };
    }

    return data;
  } catch (error) {
    return {
      success: false,
      errors: ['Network error. Please try again.'],
    };
  }
}

// Logout user
export async function logout(): Promise<AuthResponse> {
  try {
    const response = await authFetch('/users/sign_out', {
      method: 'DELETE',
    });

    const data = await response.json();
    return data;
  } catch (error) {
    return {
      success: false,
      errors: ['Logout failed'],
    };
  }
}

// Check current user authentication status
export async function getCurrentUser(): Promise<{ authenticated: boolean; user?: User }> {
  try {
    const response = await authFetch('/api/current_user');

    if (!response.ok) {
      return { authenticated: false };
    }

    const data = await response.json();
    return data;
  } catch (error) {
    return { authenticated: false };
  }
}
